import { BudgetScenarioImpactResult, BudgetScenarioWorktypeOption } from '@callmart/types';
import { ScenarioInputsPayload } from './budgetScenario';
import api from './index';

const BASE = '/budget-scenarios';

export interface ScenarioContactValue {
    worktypeId: number;
    worktypeName: string | null;
    month: string; // 'YYYY-MM-DD'
    contactsDelta: number;
}

/** One worktype's training block (CALL-783 Stage 4) — the board's Kontakte grid puts it in front of the
 *  monthly deltas: 10 agents × 8h × 24 days × €19 + €24 000 = €60 480.
 *
 *  Stored in its own table beside the deltas and joined by worktype, so a worktype may have training with
 *  no deltas or deltas with no training; a worktype with no entry here has none configured (all zero). */
export interface ScenarioContactConfig {
    worktypeId: number;
    agents: number;
    trainingDays: number;
    costPerHour: number;
    /** The one-off "Trainerressource" — named `trainerCost` on the wire, as on the shift row. */
    trainerCost: number;
    /** The training allocation window (CALL-801); null trainingStart = walk from the scenario start. */
    trainingStart: string | null;
    trainingEnd: string | null;
}

export interface ScenarioContactValuesResponse {
    /** The scenario's `updatedAt` AS OF this read, so the form's next PATCH sends the revision that belongs
     *  to THESE rows. It cannot be taken from the scenario query instead: the two queries refetch
     *  independently, and a colleague committing between them would leave the form holding old rows under a
     *  fresh revision — a save that passes the guard and then destroys their work. */
    revision: string;
    values: ScenarioContactValue[];
    configs: ScenarioContactConfig[];
    availableWorktypes: BudgetScenarioWorktypeOption[];
}

/** `versionId` is the version the OPTION lists must describe — the DRAFT one while the detail form's
 *  Budget select is pointed elsewhere, so the pickers stay in step with the impact preview (which is
 *  already priced against that version). Omitted, the server answers from the scenario's stored one. */
export const getScenarioContactValues = async (
    scenarioId: number,
    versionId?: number
): Promise<ScenarioContactValuesResponse> => {
    return api.get(`${BASE}/${scenarioId}/contact-values`, { params: { versionId } }).then((res) => res.data);
};

export const getScenarioImpact = async (scenarioId: number): Promise<BudgetScenarioImpactResult> => {
    return api.get(`${BASE}/${scenarioId}/impact`).then((res) => res.data);
};

/** An unsaved draft to compute against: the inputs plus the three pieces of metadata that decide what the
 *  impact of those inputs even IS — the period (the month axis) and the budget version (the baseline).
 *
 *  The version has to travel with the draft because the detail form can re-point a scenario at any sibling
 *  version of its bucket, and the baselines genuinely differ: forecast volumes are version-scoped, and a
 *  released snapshot's configs are frozen and never extrapolated. Left off, the server answered from the
 *  STORED version — so a version-only change read as "switching budget changes nothing" and Save then
 *  produced other figures. Name, description and type cannot change any number, so they stay out. */
export interface ScenarioImpactPreviewPayload {
    budgetVersionId?: number;
    startDate?: string; // 'YYYY-MM-DD'
    endDate?: string; // 'YYYY-MM-DD'
    inputs: ScenarioInputsPayload;
}

/** Impact of inputs that have NOT been saved (CALL-783). Writes nothing — the calc functions are pure, so
 *  the server can run them against a draft and the same figures appear here as after Save. */
export const previewScenarioImpact = async (
    scenarioId: number,
    payload: ScenarioImpactPreviewPayload
): Promise<BudgetScenarioImpactResult> => {
    return api.post(`${BASE}/${scenarioId}/impact/preview`, payload).then((res) => res.data);
};
